import { useState } from 'react';

import { Box, Button, FormikField as Field, Input, Text } from '@betterment/samba-react';
import { Form, Formik, type FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { useFeatureFlag } from '../../feature-flag';
import { ModalSectionRow } from '../../modal-section';
import { useAccountSettings } from '../AccountSettingsContext';

type Props = {
  name?: string;
  accountDescription?: string;
};

const schema = Yup.object().shape({
  name: Yup.string().trim().required("Name can't be blank"),
});

export const EditNameForm: React.FC<Props> = ({ name, accountDescription }) => {
  const { isEditable, handleNameUpdate } = useAccountSettings();
  const { enabled: isAccountNamingEnabled, loading: isAccountNamingLoading } = useFeatureFlag({
    splitName: 'retail.account_naming_q1_2026_enabled',
    context: 'AccountSettingsModal',
  });

  const [shouldShowNameForm, setShouldShowNameForm] = useState(false);

  if (isAccountNamingLoading || !isAccountNamingEnabled) return null;

  const handleNameFormSubmit = async (values: { name: string }, { setFieldError }: FormikHelpers<{ name: string }>) => {
    const errorMessage = await handleNameUpdate?.(values.name);
    if (errorMessage) {
      setFieldError('name', errorMessage);
      return;
    }
    setShouldShowNameForm(false);
  };

  const handleNameReset = async () => {
    await handleNameUpdate?.();
    setShouldShowNameForm(false);
  };

  return (
    <>
      {!shouldShowNameForm && (
        <ModalSectionRow>
          <ModalSectionRow.LeadingContent>
            <Text variant="text50" color="text.weak">
              Account name
            </Text>
            <Text>{name || accountDescription}</Text>
          </ModalSectionRow.LeadingContent>
          {isEditable && (
            <ModalSectionRow.TrailingContent>
              <Button variant="textPrimary" onClick={() => setShouldShowNameForm(true)}>
                Edit
              </Button>
            </ModalSectionRow.TrailingContent>
          )}
        </ModalSectionRow>
      )}
      {handleNameUpdate && shouldShowNameForm && (
        <ModalSectionRow>
          <Box css={{ width: '100%' }}>
            <Formik
              onSubmit={handleNameFormSubmit}
              initialValues={{ name: name || accountDescription || '' }}
              validationSchema={schema}
              validateOnChange={false}
            >
              {({ isSubmitting }) => (
                <Form>
                  <Field name="name">
                    <Field.Label>Account name</Field.Label>
                    <Box
                      css={{
                        display: 'flex',
                        justifyContent: 'stretch',
                        alignItems: 'start',
                        gap: 'space.200',
                        mt: 'space.100',
                      }}
                    >
                      <Box css={{ width: '100%' }}>
                        <Field.Control>
                          <Input type="text" aria-label="name" />
                        </Field.Control>
                        {name && name !== accountDescription && (
                          <Text variant="text50" color="text.weak">
                            Reset to default name:{' '}
                            <Button variant="textPrimary" onClick={() => void handleNameReset()}>
                              {accountDescription}
                            </Button>
                          </Text>
                        )}
                      </Box>
                      <Button type="submit" loading={isSubmitting}>
                        Save
                      </Button>
                    </Box>
                    <Field.ErrorMessage />
                  </Field>
                </Form>
              )}
            </Formik>
          </Box>
        </ModalSectionRow>
      )}
    </>
  );
};
